import React, { ComponentProps, forwardRef, useEffect, useState } from 'react'
import { useDebounce } from 'react-use'
import omit from 'lodash/omit'
import AutocompleteBase from 'transfix-ui/components/AutocompleteBase'
import {
  TInputChangeHandler,
  useAutocompleteInputState,
  useAutocompleteLazyQuery,
} from 'transfix-ui/components/AutocompleteBase/hooks'
import {
  IAutocompleteBaseOption,
  TAutocompleteBaseChangeHandler,
} from 'transfix-ui/components/AutocompleteBase/types'
import { IGetAutocompleteOptionsConfig } from 'transfix-ui/components/AutocompleteBase/utils/getAutocompleteOptions'
import { isAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase/utils/typeGuards'
import { getOptionLabel } from 'transfix-ui/components/AutocompleteFacility/utils'
import { Maybe } from 'types/graphqlTypes'
import { ISearchFacilityFragment } from './graphql/SearchFacilityFragment'
import { useShipperFacilitiesLazyQuery } from './graphql/ShipperFacilities'

const getComputedValue = (
  options: IAutocompleteBaseOption[],
  value: string[] | null
): IAutocompleteBaseOption[] | null => {
  const selectedOptions = options.filter(({ id }) => value?.includes(id))
  return selectedOptions || null
}

export interface IAutocompleteFacilityProps
  extends Omit<
    ComponentProps<typeof AutocompleteBase>,
    'value' | 'onChange' | 'options' | 'loading'
  > {
  value: string[] | null
  shipperApplicationIds: string[]
  required?: boolean
  onChange: (newVal: string[] | null) => void
  inputError?: boolean
  inputValidationMessage?: string
  dataTest?: string
}

type TShipperFacility = NonNullable<ISearchFacilityFragment['shipperFacilities']>[number] & {
  facility: Omit<ISearchFacilityFragment, 'shipperFacilities'>
}

export const AutocompleteFacility = forwardRef<HTMLInputElement, IAutocompleteFacilityProps>(
  (
    {
      clearOnBlur = false,
      dataTest = 'AutocompleteFacility',
      inputError,
      inputValidationMessage,
      label = 'Facility',
      onChange,
      placeholder = 'Search for a facility',
      required,
      shipperApplicationIds,
      value: valueProp,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useAutocompleteInputState('')
    const [refetch, { options = [], loading }] = useAutocompleteLazyQuery(
      useShipperFacilitiesLazyQuery,
      {
        variables: {
          shipperIds: shipperApplicationIds,
        },
      },
      {
        getOptionsConfig: data => {
          const { paginatedFacilities } = data || {}
          const { nodes: facilities } = paginatedFacilities || {}

          // Since a facility can have multiple shipperFacilities, this groups shipperFacilities by shipper
          // so that the correct uuid can be selected.
          const groups = shipperApplicationIds.map(shipperId => {
            const shipperApplicationFacilities: TShipperFacility[] = []
            let shipperName = ''

            facilities?.forEach((facility: Maybe<ISearchFacilityFragment>) => {
              const { shipperFacilities } = facility || {}
              shipperFacilities?.forEach(shipperFacility => {
                if (shipperFacility.shipperApplicationId === shipperId) {
                  shipperApplicationFacilities.push({
                    ...shipperFacility,
                    facility: omit(facility, 'shipperFacilities'),
                  })
                  if (!shipperName) shipperName = shipperFacility?.shipperApplication?.name || ''
                }
              })
            })

            return {
              shipperName,
              shipperFacilities: shipperApplicationFacilities,
            }
          })

          return groups.map((shipper, index) => {
            const { shipperFacilities, shipperName } = shipper
            return {
              groupIndex: index,
              groupName: shipperName,
              options: shipperFacilities,
              mapOption: ({ facility, uuid }: Partial<TShipperFacility>) => {
                const { location, name } = facility || {}
                const { city, stateCode } = location || {}
                const text = getOptionLabel(name, city, stateCode)

                return {
                  id: uuid,
                  text,
                }
              },
            }
          }) as IGetAutocompleteOptionsConfig<TShipperFacility>[]
        },
      }
    )

    /**
     * Refetch when the shipper ids change
     */
    useDebounce(
      () => {
        if (shipperApplicationIds?.length) {
          refetch()
        }
      },
      250,
      [shipperApplicationIds]
    )

    const changeHandler: TAutocompleteBaseChangeHandler = (_, newVal) => {
      if (Array.isArray(newVal)) {
        onChange(
          newVal.map(val => {
            if (isAutocompleteBaseOption(val)) {
              return val.id
            }

            return val
          })
        )
      } else {
        onChange(null)
      }
    }

    const inputChangeHandler: TInputChangeHandler = (e, newVal) => {
      /**
       * MUI triggers an onChange event with `null` value when
       * trying to press delete/backspace when there's a value
       *
       * Because this is an invalid event, we ignore it
       */
      if (e && shipperApplicationIds?.length) {
        setInputValue(e, newVal)
      }
    }

    const value = getComputedValue(options, valueProp)

    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [shipperError, setShipperError] = useState<boolean>(true)
    const [showShipperError, setShowShipperError] = useState<boolean>(false)
    const showErrorState = inputError || (shipperError && showShipperError)
    const errorMessage =
      shipperError && showShipperError
        ? 'Select a Shipper first then a Facility.'
        : inputValidationMessage

    // Prevents opening of autocomplete when there is an error message.
    useEffect(() => {
      if (shipperError) {
        setIsOpen(false)
      }
    }, [shipperError])

    // There is a shipperError if there aren't any shipper id's provided.
    useEffect(() => {
      setShipperError(!shipperApplicationIds?.length)
    }, [shipperApplicationIds?.length])

    // Error should be shown when a user attempts to click on the facilities autocomplete and has not yet selected any Shippers.
    const shipperFacilitiesFocusHandler = () => {
      setShowShipperError(!shipperApplicationIds?.length)
    }

    const openHandler = () => {
      if (!inputError && !shipperError) {
        setIsOpen(true)
      }
    }

    const closeHandler = () => setIsOpen(false)

    return (
      <AutocompleteBase
        {...props}
        multiple
        label={label}
        data-test={dataTest}
        ref={ref}
        value={value}
        onChange={changeHandler}
        inputValue={inputValue}
        onInputChange={inputChangeHandler}
        options={options}
        loading={loading}
        placeholder={placeholder}
        loadingText='Loading...'
        selectOnFocus
        autoSelect
        clearOnBlur={clearOnBlur}
        required={required}
        inputError={showErrorState}
        inputValidationMessage={errorMessage}
        open={isOpen}
        onOpen={openHandler}
        onClose={closeHandler}
        onFocus={shipperFacilitiesFocusHandler}
      />
    )
  }
)
